import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Advertisement } from '@/types';
import { useCommunityContext } from '@/providers/community-context';
import { CampaignForm } from './components/CampaignForm';
import type { CampaignFormValues } from './components/campaign-schema';
import axiosInstance from '@/lib/axios';
import { toast } from 'sonner';
import { postMultipartImageUpload } from '@/lib/vertex-upload';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle as CardHeaderTitle,
} from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import useSWR from 'swr';

export default function CreateCampaignPage() {
  const navigate = useNavigate();
  const { community } = useCommunityContext();
  const { adId } = useParams<{ adId: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, isLoading, isValidating } = useSWR(
    community?.id ? `/api/communities/${community.id}/advertisements` : null,
    (url) => axiosInstance.get(url).then((res) => res.data)
  );

  const ad = data?.advertisements?.find((a: Advertisement) => a.id === adId);

  const onSubmit = async (formData: CampaignFormValues) => {
    if (!community?.id || !adId) return;

    try {
      setIsSubmitting(true);
      const payload: {
        advertisementId: string;
        type: string;
        startDateTime?: string;
        endDateTime?: string;
        horizontalMediaUrl?: string;
        verticalMediaUrl?: string;
      } = {
        advertisementId: adId,
        type: formData.type,
      };
      if (formData.startDateTime) {
        payload.startDateTime = new Date(formData.startDateTime).toISOString();
      }
      if (formData.endDateTime) {
        payload.endDateTime = new Date(formData.endDateTime).toISOString();
      }
      let horizontalMediaUrl: string | undefined;
      if (formData.horizontalMedia && formData.horizontalMedia instanceof File) {
        const uploadData = new FormData();
        uploadData.append('file', formData.horizontalMedia);
        uploadData.append('folder', 'advertisement-campaigns');
        const result = await postMultipartImageUpload(uploadData);
        horizontalMediaUrl = result.url;
      }

      let verticalMediaUrl: string | undefined;
      if (formData.verticalMedia && formData.verticalMedia instanceof File) {
        const uploadData = new FormData();
        uploadData.append('file', formData.verticalMedia);
        uploadData.append('folder', 'advertisement-campaigns');
        const result = await postMultipartImageUpload(uploadData);
        verticalMediaUrl = result.url;
      }

      if (horizontalMediaUrl) {
        payload.horizontalMediaUrl = horizontalMediaUrl;
      }
      if (verticalMediaUrl) {
        payload.verticalMediaUrl = verticalMediaUrl;
      }

      await axiosInstance.post(`/api/communities/${community.id}/advertisement-campaigns`, payload);

      toast.success('Campaign created successfully');
      navigate(`/${community.id}/advertisement`);
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast.error('Failed to create campaign');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || (isValidating && !ad)) {
    return (
      <div className="flex justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!ad) {
    return <div className="text-center p-12 text-muted-foreground">Advertisement not found.</div>;
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <Card className="w-full border-0 bg-card shadow-none md:shadow-2xl animate-in slide-in-from-bottom-5 duration-300">
        <CardHeader className="px-3 md:px-6">
          <CardHeaderTitle className="text-2xl font-black tracking-tight text-foreground">
            New Campaign
          </CardHeaderTitle>
          <CardDescription className="text-muted-foreground">
            Schedule and place this ad. You can create multiple campaigns for the same ad.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-3 md:px-6">
          <div className="mb-6 mx-6 flex gap-4 items-center rounded-xl border border-border/40 bg-muted/30 p-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Ad
              </p>
              <p className="font-semibold text-foreground">{ad.title}</p>
            </div>
          </div>
          <CampaignForm
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            onCancel={() => navigate(-1)}
          />
        </CardContent>
      </Card>
    </div>
  );
}
